<div class="register-page">
  <div class="register-wrapper">

    <!-- Brand Header -->
    <div class="brand-header">
      <div class="logo-box">
        <img src="assets/logo-jjames.webp" alt="JJames Logo" class="brand-logo">
      </div>
      <h1 class="page-title">Create Your Account</h1>
      <p class="page-subtitle">Join JJames Smart Parking for quick access and automated parking</p>
    </div>

    <!-- Registration Card -->
    <div class="register-card">
      <form [formGroup]="registerForm" (ngSubmit)="onSubmit()" class="register-form">

        <!-- Error Alert Banner -->
        @if (formErrorMessage()) {
          <div class="form-alert error-alert">
            <mat-icon class="alert-icon">error_outline</mat-icon>
            <div class="alert-content">
              <span class="alert-title">Registration Failed</span>
              <p class="alert-text">{{ formErrorMessage() }}</p>
              @if (isEmailAlreadyRegistered()) {
                <button type="button" class="alert-action-btn" (click)="navigateToLogin()">
                  Sign in to your account ➔
                </button>
              }
            </div>
          </div>
        }

        <!-- Name Row -->
        <div class="form-row two-cols">
          <div class="form-group">
            <label class="form-label" for="firstName">First Name</label>
            <div class="input-wrapper" [class.invalid]="isFieldInvalid('firstName')">
              <mat-icon class="input-icon">person</mat-icon>
              <input
                id="firstName"
                type="text"
                formControlName="firstName"
                placeholder="First name"
                class="form-input"
                autocomplete="given-name">
            </div>
            @if (isFieldInvalid('firstName')) {
              <span class="field-error">{{ getFieldError('firstName') }}</span>
            }
          </div>

          <div class="form-group">
            <label class="form-label" for="lastName">Last Name</label>
            <div class="input-wrapper" [class.invalid]="isFieldInvalid('lastName')">
              <mat-icon class="input-icon">person</mat-icon>
              <input
                id="lastName"
                type="text"
                formControlName="lastName"
                placeholder="Last name"
                class="form-input"
                autocomplete="family-name">
            </div>
            @if (isFieldInvalid('lastName')) {
              <span class="field-error">{{ getFieldError('lastName') }}</span>
            }
          </div>
        </div>

        <!-- Email -->
        <div class="form-group">
          <label class="form-label" for="email">Email Address</label>
          <div class="input-wrapper" [class.invalid]="isFieldInvalid('email')">
            <mat-icon class="input-icon">email</mat-icon>
            <input
              id="email"
              type="email"
              formControlName="email"
              placeholder="name@example.com"
              class="form-input"
              autocomplete="email">
          </div>
          @if (isFieldInvalid('email')) {
            <span class="field-error">{{ getFieldError('email') }}</span>
          }
        </div>

        <!-- Password -->
        <div class="form-group">
          <label class="form-label" for="password">Password</label>
          <div class="input-wrapper" [class.invalid]="isFieldInvalid('password')">
            <mat-icon class="input-icon">lock</mat-icon>
            <input
              id="password"
              [type]="showPassword() ? 'text' : 'password'"
              formControlName="password"
              placeholder="Create a strong password"
              class="form-input"
              autocomplete="new-password">
            <button
              type="button"
              class="toggle-pwd-btn"
              (click)="togglePasswordVisibility()"
              [attr.aria-label]="showPassword() ? 'Hide password' : 'Show password'">
              <mat-icon>{{ showPassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
          </div>
          @if (isFieldInvalid('password')) {
            <span class="field-error">{{ getFieldError('password') }}</span>
          }
        </div>

        <!-- Confirm Password -->
        <div class="form-group">
          <label class="form-label" for="confirmPassword">Confirm Password</label>
          <div class="input-wrapper" [class.invalid]="isFieldInvalid('confirmPassword')">
            <mat-icon class="input-icon">lock</mat-icon>
            <input
              id="confirmPassword"
              [type]="showConfirmPassword() ? 'text' : 'password'"
              formControlName="confirmPassword"
              placeholder="Confirm your password"
              class="form-input"
              autocomplete="new-password">
            <button
              type="button"
              class="toggle-pwd-btn"
              (click)="toggleConfirmPasswordVisibility()"
              [attr.aria-label]="showConfirmPassword() ? 'Hide password' : 'Show password'">
              <mat-icon>{{ showConfirmPassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
          </div>
          @if (isFieldInvalid('confirmPassword')) {
            <span class="field-error">{{ getFieldError('confirmPassword') }}</span>
          }
        </div>

        <!-- Password Checklist Box -->
        <div class="pwd-requirements-box">
          <span class="requirements-title">Password must include:</span>
          <div class="requirements-grid">
            <div class="req-item" [class.met]="hasMinLength">
              <mat-icon class="req-icon">{{ hasMinLength ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
              <span>At least 8 characters</span>
            </div>
            <div class="req-item" [class.met]="hasUpperAndLower">
              <mat-icon class="req-icon">{{ hasUpperAndLower ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
              <span>Upper & lower case letters</span>
            </div>
            <div class="req-item" [class.met]="hasNumeric">
              <mat-icon class="req-icon">{{ hasNumeric ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
              <span>At least one number</span>
            </div>
            <div class="req-item" [class.met]="hasSpecialChar">
              <mat-icon class="req-icon">{{ hasSpecialChar ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
              <span>At least one special character</span>
            </div>
          </div>
        </div>

        <!-- Notification Preference Checkbox -->
        <div class="checkbox-row">
          <label class="custom-checkbox-label">
            <input type="checkbox" formControlName="enableMarketingNotifications" class="custom-checkbox">
            <span class="checkbox-text">Send me email updates and parking announcements</span>
          </label>
        </div>

        <!-- Terms Checkbox -->
        <div class="checkbox-row">
          <label class="custom-checkbox-label">
            <input type="checkbox" formControlName="agreeToTerms" class="custom-checkbox">
            <span class="checkbox-text">
              I agree to the <a routerLink="/legal" target="_blank" class="inline-link">Terms of Service</a> and <a routerLink="/legal" target="_blank" class="inline-link">Privacy Policy</a>
            </span>
          </label>
          @if (isFieldInvalid('agreeToTerms')) {
            <span class="field-error checkbox-error">{{ getFieldError('agreeToTerms') }}</span>
          }
        </div>

        <!-- Submit Button -->
        <div class="submit-section">
          <jjames-button
            variant="primary"
            size="lg"
            matIconLeft="person_add"
            [disabled]="registerForm.invalid || isLoading()"
            [loading]="isLoading()"
            (clicked)="onSubmit()">
            Create Account
          </jjames-button>
        </div>

        <!-- Sign In Prompt -->
        <div class="login-footer">
          <span>Already have an account?</span>
          <button type="button" class="sign-in-btn" (click)="navigateToLogin()">
            Sign in here
          </button>
        </div>

      </form>
    </div>

  </div>
</div>